import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowLeft, RefreshCw, Play, Pause, Activity, Timer, AlertTriangle, BellRing } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  http: 'HTTP Health Check', ping: 'PING (ICMP)', tcp: 'TCP Port',
  status: 'Status de página', redirect: 'Redirecionamento HTTP→HTTPS',
};

const fmtUptime = (v) => (v === null || v === undefined ? '—' : `${v.toFixed(2)}%`);
const uptimeClass = (v) => (v === null || v === undefined ? '' : v >= 99.5 ? 'text-green-600' : v >= 95 ? 'text-amber-500' : 'text-red-500');
const fmtDuration = (s) => {
  if (!s && s !== 0) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  return `${(s / 3600).toFixed(1)}h`;
};

export default function MonitorDetailPage() {
  const { monitorId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuthStore();
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/webmonitor/monitors/${monitorId}?hours=${hours}`,
        { headers: getAuthHeader() });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar monitor');
    } finally {
      setLoading(false);
    }
  }, [monitorId, hours, getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const act = async (action) => {
    try {
      const res = await axios.post(`${API}/webmonitor/monitors/${monitorId}/${action}`, {},
        { headers: getAuthHeader() });
      toast.success(res.data.message || 'Verificação executada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na ação');
    }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-80 w-full" /></div>;
  if (!data) return null;

  const m = data.monitor;
  const chart = (data.checks || []).map(c => ({
    t: new Date(c.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    latency: c.latency_ms || 0,
    up: c.status === 'up' ? 100 : 0,
  }));

  return (
    <div className="space-y-6" data-testid="monitor-detail-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/webmonitor')}
            data-testid="back-to-webmonitor" className="-ml-2 mb-1">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Web Monitor
          </Button>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${m.paused ? 'bg-zinc-400' : m.last_status === 'up' ? 'bg-green-500' : m.last_status === 'down' ? 'bg-red-500' : 'bg-zinc-400'}`} />
            {m.name}
          </h2>
          <p className="text-sm text-muted-foreground font-mono">{m.target}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <Badge variant="secondary">{TYPE_LABELS[m.type] || m.type}</Badge>
            <Badge variant="outline">{m.org_name}</Badge>
            <Badge variant="outline">a cada {Math.round((m.interval_seconds || 300) / 60)} min</Badge>
            {m.paused && <Badge variant="outline">pausado</Badge>}
            {m.alerting === false && <Badge variant="outline" title={m.mode_reason || ''}>informativo</Badge>}
            {m.auto_created && <Badge variant="outline">automático</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          {[24, 168, 720].map(h => (
            <Button key={h} size="sm" variant={hours === h ? 'secondary' : 'ghost'}
              onClick={() => setHours(h)} data-testid={`period-${h}`}>
              {h === 24 ? '24h' : h === 168 ? '7d' : '30d'}
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={() => act('run')} data-testid="detail-run-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Verificar agora
          </Button>
          <Button size="sm" variant="outline" onClick={() => act(m.paused ? 'resume' : 'pause')}
            data-testid="detail-pause-btn">
            {m.paused ? <><Play className="h-4 w-4 mr-1.5" /> Retomar</> : <><Pause className="h-4 w-4 mr-1.5" /> Pausar</>}
          </Button>
          {m.alerting === false && (
            <Button size="sm" onClick={() => act('enable-alerts')} data-testid="detail-enable-alerts-btn">
              <BellRing className="h-4 w-4 mr-1.5" /> Ativar alertas
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-5">
        {[
          { label: 'Uptime 24h', value: fmtUptime(m.uptime_24h), cls: uptimeClass(m.uptime_24h), icon: Activity },
          { label: 'Uptime 7d', value: fmtUptime(m.uptime_7d), cls: uptimeClass(m.uptime_7d), icon: Activity },
          { label: 'Uptime 30d', value: fmtUptime(m.uptime_30d), cls: uptimeClass(m.uptime_30d), icon: Activity },
          { label: 'Latência média', value: data.avg_latency_ms ? `${data.avg_latency_ms} ms` : '—', icon: Timer },
          { label: 'Incidentes', value: (data.incidents || []).length, icon: AlertTriangle },
        ].map(card => (
          <Card key={card.label} data-testid={`kpi-${card.label}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className={`text-2xl font-bold ${card.cls || ''}`}>{card.value}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{card.label}</div>
              </div>
              <card.icon className="h-5 w-5 text-muted-foreground" />
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Latência e disponibilidade</CardTitle></CardHeader>
        <CardContent>
          {chart.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">
              Sem verificações registradas no período
            </p>
          ) : (
            <div className="h-64" data-testid="monitor-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="t" tick={{ fontSize: 11 }} minTickGap={40} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11 }} unit=" ms" />
                  <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip />
                  <Line yAxisId="l" type="monotone" dataKey="latency" name="Latência (ms)"
                    stroke="#0ea5e9" dot={false} strokeWidth={2} />
                  <Line yAxisId="r" type="stepAfter" dataKey="up" name="Disponível (%)"
                    stroke="#22c55e" dot={false} strokeWidth={1} opacity={0.6} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Incidentes do monitor</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Início</TableHead><TableHead>Duração</TableHead>
                <TableHead>Mensagem</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(data.incidents || []).slice(0, 20).map(i => (
                  <TableRow key={i.id} data-testid={`incident-row-${i.id}`}>
                    <TableCell className="text-sm">{new Date(i.started_at).toLocaleString('pt-BR')}</TableCell>
                    <TableCell className="text-sm">{fmtDuration(i.duration_seconds)}</TableCell>
                    <TableCell className="text-sm truncate max-w-[220px]">{i.message}</TableCell>
                    <TableCell>
                      <Badge variant={i.status === 'open' ? 'destructive' : 'secondary'}>
                        {i.status === 'open' ? 'Em aberto' : 'Resolvido'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {(data.incidents || []).length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-sm text-muted-foreground">
                    Nenhum incidente registrado
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Últimas verificações</CardTitle></CardHeader>
          <CardContent className="p-0 max-h-[420px] overflow-y-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Momento</TableHead><TableHead>Status</TableHead>
                <TableHead>Latência</TableHead><TableHead>Mensagem</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {[...(data.checks || [])].reverse().slice(0, 100).map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="text-sm">{new Date(c.created_at).toLocaleString('pt-BR')}</TableCell>
                    <TableCell>
                      <span className={`inline-block h-2.5 w-2.5 rounded-full ${c.status === 'up' ? 'bg-green-500' : c.status === 'down' ? 'bg-red-500' : 'bg-zinc-400'}`} />
                    </TableCell>
                    <TableCell className="text-sm">{c.latency_ms ? `${c.latency_ms} ms` : '—'}</TableCell>
                    <TableCell className="text-sm truncate max-w-[200px]">{c.message}</TableCell>
                  </TableRow>
                ))}
                {(data.checks || []).length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-sm text-muted-foreground">
                    Sem verificações no período
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
